import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n";

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const switchLang = (code) => {
    setLang(code);
    const rest = location.pathname.replace(/^\/(da|en|sw)/, "");
    navigate(`/${code}${rest}${location.hash}`);
  };

  const links = [
    { to: `/${lang}/#ventures`, label: t.nav.ventures, id: "nav-ventures" },
    { to: `/${lang}/#about`, label: t.nav.about, id: "nav-about" },
    { to: `/${lang}/#contact`, label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-[80] bg-bone/80 backdrop-blur-xl border-b border-ebony/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to={`/${lang}`} data-testid="nav-logo" className="flex items-baseline gap-2 group" onClick={() => setOpen(false)}>
          <span className="font-serif text-xl md:text-2xl font-semibold tracking-tight text-ebony">
            Hallomedics<span className="text-terracotta">.</span>
          </span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-ebony/50 group-hover:text-terracotta transition-colors duration-300">KEDE</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={l.id}
              className="text-sm font-light tracking-wide text-ebony/70 hover:text-terracotta transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center rounded-full border border-ebony/15 p-0.5" data-testid="language-toggle">
            {["da", "en", "sw"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => switchLang(code)}
                className={`px-3 py-1 rounded-full text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${
                  lang === code ? "bg-terracotta text-bone" : "text-ebony/60 hover:text-ebony"
                }`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </nav>

        <button
          className="md:hidden text-ebony"
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-ebony/10 bg-bone px-6 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`mobile-${l.id}`}
              onClick={() => setOpen(false)}
              className="block font-serif text-2xl text-ebony"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex items-center gap-2 pt-2">
            {["da", "en", "sw"].map((code) => (
              <button
                key={code}
                data-testid={`mobile-lang-${code}`}
                onClick={() => switchLang(code)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-widest transition-colors duration-300 ${
                  lang === code ? "bg-terracotta text-bone" : "border border-ebony/15 text-ebony/60"
                }`}
              >
                {code === "da" ? "Dansk" : code === "en" ? "English" : "Swahili"}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
